import React, { useMemo, useState } from 'react';
import Card from '../components/common/Card';
import Badge from '../components/common/Badge';
import { BarChart3, Calendar, ChevronDown, Info, Filter, Download, Search, TrendingUp, TrendingDown } from 'lucide-react';
import { deputyPerformance, performanceMetrics } from '../utils/dummyData';
const PerformanceCockpit = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUnit, setSelectedUnit] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [minQuality, setMinQuality] = useState(0);
  const [sortBy, setSortBy] = useState<'quality' | 'onTime' | 'volume'>('quality');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [hoveredMonth, setHoveredMonth] = useState<number | null>(null);
  const [selectedTrendMetric, setSelectedTrendMetric] = useState<'all' | 'response' | 'products' | 'coverage'>('all');
  const filteredPerformance = deputyPerformance.filter(unit => {
    const matchesSearch = unit.unit.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesUnit = selectedUnit === 'all' || selectedUnit === 'deputi' && unit.unit.includes('Deputi') || selectedUnit === 'binda' && unit.unit.includes('BINDA');
    const matchesStatus = selectedStatus === 'all' || unit.status === selectedStatus;
    const matchesQuality = unit.quality >= minQuality;
    return matchesSearch && matchesUnit && matchesStatus && matchesQuality;
  }).sort((a, b) => {
    const multiplier = sortOrder === 'asc' ? 1 : -1;
    if (sortBy === 'quality') return (a.quality - b.quality) * multiplier;
    if (sortBy === 'onTime') return (a.onTime - b.onTime) * multiplier;
    return (a.reportVolume - b.reportVolume) * multiplier;
  });
  // Calculate aggregate statistics
  const aggregateStats = useMemo(() => {
    const total = filteredPerformance.length;
    const avgQuality = filteredPerformance.reduce((sum, u) => sum + u.quality, 0) / total;
    const avgOnTime = filteredPerformance.reduce((sum, u) => sum + u.onTime, 0) / total;
    const totalReports = filteredPerformance.reduce((sum, u) => sum + u.reportVolume, 0);
    const goodStatus = filteredPerformance.filter(u => u.status === 'good').length;
    const attentionStatus = filteredPerformance.filter(u => u.status === 'attention').length;
    return {
      avgQuality: avgQuality.toFixed(1),
      avgOnTime: avgOnTime.toFixed(1),
      totalReports,
      goodStatus,
      attentionStatus
    };
  }, [filteredPerformance]);
  // Performance trend data
  const trendData = {
    months: ['Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug'],
    response: [82, 85, 84, 88, 87, 90],
    products: [78, 82, 85, 87, 89, 92],
    coverage: [85, 87, 86, 90, 92, 94]
  };
  return <div className="space-y-6" data-id="element-838">
      <div className="flex items-center justify-between" data-id="element-839">
        <h1 className="text-2xl font-bold text-white" data-id="element-840">
          INTEL-Performance Cockpit
        </h1>
        <div className="flex items-center space-x-3" data-id="element-841">
          <button className="flex items-center px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg border border-gray-700" data-id="element-842">
            <Download className="h-4 w-4 mr-2" data-id="element-843" />
            Export Report
          </button>
          <div className="flex items-center px-2 py-1 bg-gray-800 rounded text-xs text-gray-400 border border-gray-700" data-id="element-844">
            <Calendar className="h-3 w-3 mr-1" data-id="element-845" />
            <span data-id="element-846">Last 30 Days</span>
            <ChevronDown className="h-3 w-3 ml-1" data-id="element-847" />
          </div>
        </div>
      </div>
      {/* Enhanced Top Metric Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4" data-id="element-848">
        <Card className="p-4" data-id="element-849">
          <div className="flex justify-between items-start" data-id="element-850">
            <div data-id="element-851">
              <p className="text-sm text-gray-400" data-id="element-852">Average Quality</p>
              <h3 className="text-2xl font-bold text-white mt-1" data-id="element-853">
                {aggregateStats.avgQuality}%
              </h3>
            </div>
            <Badge variant="green" data-id="element-854">Good</Badge>
          </div>
          <div className="mt-3 h-1 bg-gray-700 rounded-full overflow-hidden" data-id="element-855">
            <div className="h-full bg-green-500" style={{
            width: `${aggregateStats.avgQuality}%`
          }} data-id="element-856"></div>
          </div>
          <div className="mt-2 flex justify-between items-center text-xs text-gray-400" data-id="element-857">
            <span data-id="element-858">Target: 85%</span>
            <span className="text-green-400" data-id="element-859">+2.5%</span>
          </div>
        </Card>
        <Card className="p-4" data-id="element-860">
          <div className="flex justify-between items-start" data-id="element-861">
            <div data-id="element-862">
              <p className="text-sm text-gray-400" data-id="element-863">On-Time Rate</p>
              <h3 className="text-2xl font-bold text-white mt-1" data-id="element-864">
                {aggregateStats.avgOnTime}%
              </h3>
            </div>
            <Badge variant="green" data-id="element-865">Good</Badge>
          </div>
          <div className="mt-3 h-1 bg-gray-700 rounded-full overflow-hidden" data-id="element-866">
            <div className="h-full bg-cyan-500" style={{
            width: `${aggregateStats.avgOnTime}%`
          }} data-id="element-867"></div>
          </div>
          <div className="mt-2 flex justify-between items-center text-xs text-gray-400" data-id="element-868">
            <span data-id="element-869">Target: 85%</span>
            <span className="text-green-400" data-id="element-870">+1.8%</span>
          </div>
        </Card>
        <Card className="p-4" data-id="element-871">
          <div className="flex justify-between items-start" data-id="element-872">
            <div data-id="element-873">
              <p className="text-sm text-gray-400" data-id="element-874">Total Reports</p>
              <h3 className="text-2xl font-bold text-white mt-1" data-id="element-875">
                {aggregateStats.totalReports}
              </h3>
            </div>
            <Badge variant="blue" data-id="element-876">Active</Badge>
          </div>
          <div className="mt-3 h-1 bg-gray-700 rounded-full overflow-hidden" data-id="element-877">
            <div className="h-full bg-blue-500" style={{
            width: '75%'
          }} data-id="element-878"></div>
          </div>
          <div className="mt-2 flex justify-between items-center text-xs text-gray-400" data-id="element-879">
            <span data-id="element-880">This Period</span>
            <span className="text-blue-400" data-id="element-881">+12 reports</span>
          </div>
        </Card>
        <Card className="p-4" data-id="element-882">
          <div className="flex justify-between items-start" data-id="element-883">
            <div data-id="element-884">
              <p className="text-sm text-gray-400" data-id="element-885">Good Performance</p>
              <h3 className="text-2xl font-bold text-white mt-1" data-id="element-886">
                {aggregateStats.goodStatus}
              </h3>
            </div>
            <Badge variant="green" data-id="element-887">Excellent</Badge>
          </div>
          <div className="mt-3 h-1 bg-gray-700 rounded-full overflow-hidden" data-id="element-888">
            <div className="h-full bg-green-500" style={{
            width: `${aggregateStats.goodStatus / filteredPerformance.length * 100}%`
          }} data-id="element-889"></div>
          </div>
          <div className="mt-2 flex justify-between items-center text-xs text-gray-400" data-id="element-890">
            <span data-id="element-891">Units</span>
            <span className="text-green-400" data-id="element-892">
              {(aggregateStats.goodStatus / filteredPerformance.length * 100).toFixed(0)}
              %
            </span>
          </div>
        </Card>
        <Card className="p-4" data-id="element-893">
          <div className="flex justify-between items-start" data-id="element-894">
            <div data-id="element-895">
              <p className="text-sm text-gray-400" data-id="element-896">Needs Attention</p>
              <h3 className="text-2xl font-bold text-white mt-1" data-id="element-897">
                {aggregateStats.attentionStatus}
              </h3>
            </div>
            <Badge variant="yellow" data-id="element-898">Review</Badge>
          </div>
          <div className="mt-3 h-1 bg-gray-700 rounded-full overflow-hidden" data-id="element-899">
            <div className="h-full bg-yellow-500" style={{
            width: `${aggregateStats.attentionStatus / filteredPerformance.length * 100}%`
          }} data-id="element-900"></div>
          </div>
          <div className="mt-2 flex justify-between items-center text-xs text-gray-400" data-id="element-901">
            <span data-id="element-902">Units</span>
            <span className="text-yellow-400" data-id="element-903">Action Required</span>
          </div>
        </Card>
      </div>
      {/* Enhanced Filters */}
      <Card className="p-4" data-id="element-904">
        <div className="flex flex-wrap items-center gap-3" data-id="element-905">
          <div className="flex items-center" data-id="element-906">
            <Filter className="h-4 w-4 text-gray-400 mr-2" data-id="element-907" />
            <span className="text-sm text-gray-400" data-id="element-908">Filters:</span>
          </div>
          <div className="relative flex-1 max-w-md" data-id="element-909">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" data-id="element-910" />
            <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search unit..." className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-cyan-500" data-id="element-911" />
          </div>
          <select value={selectedUnit} onChange={e => setSelectedUnit(e.target.value)} className="bg-gray-800 text-sm text-white border border-gray-700 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-cyan-500" data-id="element-912">
            <option value="all" data-id="element-913">All Units</option>
            <option value="deputi" data-id="element-914">Deputi Only</option>
            <option value="binda" data-id="element-915">BINDA Only</option>
          </select>
          <select value={selectedStatus} onChange={e => setSelectedStatus(e.target.value)} className="bg-gray-800 text-sm text-white border border-gray-700 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-cyan-500" data-id="element-916">
            <option value="all" data-id="element-917">All Status</option>
            <option value="good" data-id="element-918">Good</option>
            <option value="attention" data-id="element-919">Needs Attention</option>
            <option value="critical" data-id="element-920">Critical</option>
          </select>
          <div className="flex items-center space-x-2" data-id="element-921">
            <label className="text-sm text-gray-400" data-id="element-922">Sort By:</label>
            <select value={sortBy} onChange={e => setSortBy(e.target.value as any)} className="bg-gray-800 text-sm text-white border border-gray-700 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-cyan-500" data-id="element-923">
              <option value="quality" data-id="element-924">Quality</option>
              <option value="onTime" data-id="element-925">On-Time</option>
              <option value="volume" data-id="element-926">Volume</option>
            </select>
            <button onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')} className="p-2 bg-gray-800 hover:bg-gray-700 rounded-lg border border-gray-700" data-id="element-927">
              {sortOrder === 'asc' ? '↑' : '↓'}
            </button>
          </div>
          <div className="flex items-center space-x-2" data-id="element-928">
            <label className="text-sm text-gray-400" data-id="element-929">Min Quality:</label>
            <input type="range" min="0" max="100" value={minQuality} onChange={e => setMinQuality(Number(e.target.value))} className="w-32" data-id="element-930" />
            <span className="text-sm text-white w-12" data-id="element-931">{minQuality}%</span>
          </div>
        </div>
      </Card>
      {/* Enhanced Table - Full Width */}
      <Card data-id="element-932">
        <div className="p-4 border-b border-gray-800 flex justify-between items-center" data-id="element-933">
          <h2 className="text-lg font-semibold text-white" data-id="element-934">
            Laporan Kinerja BINDA & Deputi
          </h2>
          <div className="flex items-center space-x-2" data-id="element-935">
            <button className="p-1.5 text-gray-400 hover:text-white bg-gray-800 rounded" data-id="element-936">
              <Info className="h-4 w-4" data-id="element-937" />
            </button>
            <span className="text-sm text-gray-400" data-id="element-938">
              Showing {filteredPerformance.length} of {deputyPerformance.length}{' '}
              units
            </span>
          </div>
        </div>
        <div className="overflow-x-auto" data-id="element-939">
          <table className="w-full" data-id="element-940">
            <thead data-id="element-941">
              <tr className="bg-gray-800" data-id="element-942">
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider" data-id="element-943">
                  Unit
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-400 uppercase tracking-wider" data-id="element-944">
                  Volume Laporan
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-400 uppercase tracking-wider" data-id="element-945">
                  Kualitas
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-400 uppercase tracking-wider" data-id="element-946">
                  Ketepatan Waktu
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-400 uppercase tracking-wider" data-id="element-947">
                  Status
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-400 uppercase tracking-wider" data-id="element-948">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800" data-id="element-949">
              {filteredPerformance.map((unit, index) => <tr key={index} className="hover:bg-gray-800/50 cursor-pointer" data-id="element-950">
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-white" data-id="element-951">
                    {unit.unit}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-center" data-id="element-952">
                    <div className="flex flex-col items-center" data-id="element-953">
                      <span className="text-gray-300 font-semibold" data-id="element-954">
                        {unit.reportVolume}
                      </span>
                      <span className="text-xs text-gray-500" data-id="element-955">reports</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-center" data-id="element-956">
                    <div className="flex flex-col items-center" data-id="element-957">
                      <span className="text-sm text-gray-300 font-semibold" data-id="element-958">
                        {unit.quality}%
                      </span>
                      <div className="mt-1 w-20 h-1.5 bg-gray-700 rounded-full overflow-hidden" data-id="element-959">
                        <div className={`h-full ${unit.quality >= 85 ? 'bg-green-500' : unit.quality >= 70 ? 'bg-yellow-500' : 'bg-red-500'}`} style={{
                      width: `${unit.quality}%`
                    }} data-id="element-960"></div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-center" data-id="element-961">
                    <div className="flex flex-col items-center" data-id="element-962">
                      <span className="text-sm text-gray-300 font-semibold" data-id="element-963">
                        {unit.onTime}%
                      </span>
                      <div className="mt-1 w-20 h-1.5 bg-gray-700 rounded-full overflow-hidden" data-id="element-964">
                        <div className={`h-full ${unit.onTime >= 85 ? 'bg-green-500' : unit.onTime >= 70 ? 'bg-yellow-500' : 'bg-red-500'}`} style={{
                      width: `${unit.onTime}%`
                    }} data-id="element-965"></div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-center" data-id="element-966">
                    <Badge variant={unit.status === 'good' ? 'green' : unit.status === 'attention' ? 'yellow' : 'red'} data-id="element-967">
                      {unit.status}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-center" data-id="element-968">
                    <button className="text-cyan-400 hover:text-cyan-300 text-sm" data-id="element-969">
                      View Details
                    </button>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </Card>
      {/* Enhanced Coverage Heatmap with Animations */}
      <Card className="p-4" data-id="element-970">
        <div className="flex justify-between items-center mb-4" data-id="element-971">
          <h2 className="text-lg font-semibold text-white" data-id="element-972">Coverage Heatmap</h2>
          <BarChart3 className="h-5 w-5 text-cyan-500" data-id="element-973" />
        </div>
        <div className="bg-[#172234] h-[400px] rounded-lg flex items-center justify-center relative overflow-hidden" data-id="element-974">
          <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/0/05/Indonesia_%28orthographic_projection%29.svg/1200px-Indonesia_%28orthographic_projection%29.svg.png" alt="Indonesia Map" className="w-full h-full object-cover opacity-40" data-id="element-975" />
          <div className="absolute inset-0 bg-gradient-to-br from-green-500/20 via-yellow-500/20 to-red-500/20" data-id="element-976"></div>
          {/* Animated heat zones */}
          <div className="absolute top-1/4 right-1/3 w-12 h-12 rounded-full bg-green-500/30 animate-pulse" data-id="element-977">
            <div className="absolute inset-0 rounded-full bg-green-500/20 animate-ping" data-id="element-978"></div>
          </div>
          <div className="absolute top-1/3 left-1/4 w-16 h-16 rounded-full bg-yellow-500/30 animate-pulse" style={{
          animationDelay: '0.5s'
        }} data-id="element-979">
            <div className="absolute inset-0 rounded-full bg-yellow-500/20 animate-ping" style={{
            animationDelay: '0.5s'
          }} data-id="element-980"></div>
          </div>
          <div className="absolute bottom-1/3 right-1/4 w-20 h-20 rounded-full bg-orange-500/30 animate-pulse" style={{
          animationDelay: '1s'
        }} data-id="element-981">
            <div className="absolute inset-0 rounded-full bg-orange-500/20 animate-ping" style={{
            animationDelay: '1s'
          }} data-id="element-982"></div>
          </div>
          <div className="absolute bottom-1/4 left-1/3 w-24 h-24 rounded-full bg-red-500/30 animate-pulse" style={{
          animationDelay: '1.5s'
        }} data-id="element-983">
            <div className="absolute inset-0 rounded-full bg-red-500/20 animate-ping" style={{
            animationDelay: '1.5s'
          }} data-id="element-984"></div>
          </div>
          {/* Additional smaller animated zones */}
          <div className="absolute top-1/2 left-1/2 w-8 h-8 rounded-full bg-cyan-500/30 animate-pulse" style={{
          animationDelay: '0.3s'
        }} data-id="element-985">
            <div className="absolute inset-0 rounded-full bg-cyan-500/20 animate-ping" style={{
            animationDelay: '0.3s'
          }} data-id="element-986"></div>
          </div>
          <div className="absolute top-2/3 right-2/5 w-10 h-10 rounded-full bg-blue-500/30 animate-pulse" style={{
          animationDelay: '0.8s'
        }} data-id="element-987">
            <div className="absolute inset-0 rounded-full bg-blue-500/20 animate-ping" style={{
            animationDelay: '0.8s'
          }} data-id="element-988"></div>
          </div>
        </div>
        <div className="mt-3 flex justify-between items-center" data-id="element-989">
          <div className="flex space-x-3 text-xs text-gray-400" data-id="element-990">
            <div className="flex items-center" data-id="element-991">
              <div className="w-2 h-2 rounded-full bg-green-500 mr-1 animate-pulse" data-id="element-992"></div>
              <span data-id="element-993">High Coverage</span>
            </div>
            <div className="flex items-center" data-id="element-994">
              <div className="w-2 h-2 rounded-full bg-yellow-500 mr-1 animate-pulse" data-id="element-995"></div>
              <span data-id="element-996">Medium Coverage</span>
            </div>
            <div className="flex items-center" data-id="element-997">
              <div className="w-2 h-2 rounded-full bg-orange-500 mr-1 animate-pulse" data-id="element-998"></div>
              <span data-id="element-999">Low Coverage</span>
            </div>
            <div className="flex items-center" data-id="element-1000">
              <div className="w-2 h-2 rounded-full bg-red-500 mr-1 animate-pulse" data-id="element-1001"></div>
              <span data-id="element-1002">Critical Area</span>
            </div>
          </div>
          <button className="px-2 py-1 bg-gray-800 text-gray-300 rounded text-xs hover:bg-gray-700" data-id="element-1003">
            Details
          </button>
        </div>
      </Card>
      {/* Enhanced Interactive Performance Trends */}
      <Card className="p-4" data-id="element-1004">
        <div className="flex justify-between items-center mb-4" data-id="element-1005">
          <div data-id="element-1006">
            <h2 className="text-lg font-semibold text-white" data-id="element-1007">
              Performance Trends
            </h2>
            <p className="text-xs text-gray-400 mt-1" data-id="element-1008">
              6-month performance analysis with interactive visualization
            </p>
          </div>
          <div className="flex items-center space-x-2" data-id="element-1009">
            <select value={selectedTrendMetric} onChange={e => setSelectedTrendMetric(e.target.value as any)} className="bg-gray-800 text-sm text-white border border-gray-700 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-cyan-500" data-id="element-1010">
              <option value="all" data-id="element-1011">All Metrics</option>
              <option value="response" data-id="element-1012">Response Rate</option>
              <option value="products" data-id="element-1013">Intel Products</option>
              <option value="coverage" data-id="element-1014">Coverage</option>
            </select>
          </div>
        </div>
        <div className="h-[300px] bg-gradient-to-b from-gray-900 to-[#0a1628] rounded-lg p-6 relative" data-id="element-1015">
          {/* Grid lines */}
          <div className="absolute inset-0 flex flex-col justify-between pointer-events-none p-6" data-id="element-1016">
            {[0, 1, 2, 3, 4].map(i => <div key={i} className="w-full h-px bg-gray-800/50" data-id="element-1017"></div>)}
          </div>
          {/* Y-axis labels */}
          <div className="absolute left-0 top-0 bottom-0 flex flex-col justify-between py-6 pr-2 text-xs text-gray-500" data-id="element-1018">
            <span data-id="element-1019">100%</span>
            <span data-id="element-1020">75%</span>
            <span data-id="element-1021">50%</span>
            <span data-id="element-1022">25%</span>
            <span data-id="element-1023">0%</span>
          </div>
          {/* Chart area */}
          <div className="ml-8 h-full relative" data-id="element-1024">
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 600 250" preserveAspectRatio="none" data-id="element-1025">
              {/* Response Rate Line */}
              {(selectedTrendMetric === 'all' || selectedTrendMetric === 'response') && <>
                  <defs data-id="element-1026">
                    <linearGradient id="responseGradient" x1="0%" y1="0%" x2="0%" y2="100%" data-id="element-1027">
                      <stop offset="0%" stopColor="#22D3EE" stopOpacity="0.3" data-id="element-1028" />
                      <stop offset="100%" stopColor="#22D3EE" stopOpacity="0" data-id="element-1029" />
                    </linearGradient>
                  </defs>
                  <path d="M 0,180 L 100,150 L 200,160 L 300,120 L 400,130 L 500,100" fill="none" stroke="#22D3EE" strokeWidth="3" className="transition-all duration-300" data-id="element-1030" />
                  <path d="M 0,180 L 100,150 L 200,160 L 300,120 L 400,130 L 500,100 L 500,250 L 0,250 Z" fill="url(#responseGradient)" data-id="element-1031" />
                  {trendData.response.map((value, index) => {
                const x = index / (trendData.response.length - 1) * 500;
                const y = 250 - value / 100 * 250;
                return <circle key={`response-${index}`} cx={x} cy={y} r={hoveredMonth === index ? '8' : '5'} fill="#22D3EE" className="transition-all duration-200 cursor-pointer" onMouseEnter={() => setHoveredMonth(index)} onMouseLeave={() => setHoveredMonth(null)} data-id="element-1032" />;
              })}
                </>}
              {/* Intel Products Line */}
              {(selectedTrendMetric === 'all' || selectedTrendMetric === 'products') && <>
                  <defs data-id="element-1033">
                    <linearGradient id="productsGradient" x1="0%" y1="0%" x2="0%" y2="100%" data-id="element-1034">
                      <stop offset="0%" stopColor="#10B981" stopOpacity="0.3" data-id="element-1035" />
                      <stop offset="100%" stopColor="#10B981" stopOpacity="0" data-id="element-1036" />
                    </linearGradient>
                  </defs>
                  <path d="M 0,200 L 100,180 L 200,150 L 300,130 L 400,110 L 500,80" fill="none" stroke="#10B981" strokeWidth="3" className="transition-all duration-300" data-id="element-1037" />
                  <path d="M 0,200 L 100,180 L 200,150 L 300,130 L 400,110 L 500,80 L 500,250 L 0,250 Z" fill="url(#productsGradient)" data-id="element-1038" />
                  {trendData.products.map((value, index) => {
                const x = index / (trendData.products.length - 1) * 500;
                const y = 250 - value / 100 * 250;
                return <circle key={`products-${index}`} cx={x} cy={y} r={hoveredMonth === index ? '8' : '5'} fill="#10B981" className="transition-all duration-200 cursor-pointer" onMouseEnter={() => setHoveredMonth(index)} onMouseLeave={() => setHoveredMonth(null)} data-id="element-1039" />;
              })}
                </>}
              {/* Coverage Line */}
              {(selectedTrendMetric === 'all' || selectedTrendMetric === 'coverage') && <>
                  <defs data-id="element-1040">
                    <linearGradient id="coverageGradient" x1="0%" y1="0%" x2="0%" y2="100%" data-id="element-1041">
                      <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.3" data-id="element-1042" />
                      <stop offset="100%" stopColor="#3B82F6" stopOpacity="0" data-id="element-1043" />
                    </linearGradient>
                  </defs>
                  <path d="M 0,150 L 100,130 L 200,140 L 300,100 L 400,80 L 500,60" fill="none" stroke="#3B82F6" strokeWidth="3" className="transition-all duration-300" data-id="element-1044" />
                  <path d="M 0,150 L 100,130 L 200,140 L 300,100 L 400,80 L 500,60 L 500,250 L 0,250 Z" fill="url(#coverageGradient)" data-id="element-1045" />
                  {trendData.coverage.map((value, index) => {
                const x = index / (trendData.coverage.length - 1) * 500;
                const y = 250 - value / 100 * 250;
                return <circle key={`coverage-${index}`} cx={x} cy={y} r={hoveredMonth === index ? '8' : '5'} fill="#3B82F6" className="transition-all duration-200 cursor-pointer" onMouseEnter={() => setHoveredMonth(index)} onMouseLeave={() => setHoveredMonth(null)} data-id="element-1046" />;
              })}
                </>}
            </svg>
            {/* Hover tooltip */}
            {hoveredMonth !== null && <div className="absolute bg-gray-900 border border-gray-700 rounded-lg p-3 shadow-xl z-10 pointer-events-none" style={{
            left: `${hoveredMonth / (trendData.months.length - 1) * 100}%`,
            top: '50%',
            transform: 'translate(-50%, -50%)'
          }} data-id="element-1047">
                <p className="text-xs font-semibold text-white mb-2" data-id="element-1048">
                  {trendData.months[hoveredMonth]}
                </p>
                {(selectedTrendMetric === 'all' || selectedTrendMetric === 'response') && <div className="flex items-center justify-between mb-1" data-id="element-1049">
                    <span className="text-xs text-cyan-400 mr-3" data-id="element-1050">
                      Response Rate:
                    </span>
                    <span className="text-xs font-bold text-white" data-id="element-1051">
                      {trendData.response[hoveredMonth]}%
                    </span>
                  </div>}
                {(selectedTrendMetric === 'all' || selectedTrendMetric === 'products') && <div className="flex items-center justify-between mb-1" data-id="element-1052">
                    <span className="text-xs text-green-400 mr-3" data-id="element-1053">
                      Intel Products:
                    </span>
                    <span className="text-xs font-bold text-white" data-id="element-1054">
                      {trendData.products[hoveredMonth]}%
                    </span>
                  </div>}
                {(selectedTrendMetric === 'all' || selectedTrendMetric === 'coverage') && <div className="flex items-center justify-between" data-id="element-1055">
                    <span className="text-xs text-blue-400 mr-3" data-id="element-1056">
                      Coverage:
                    </span>
                    <span className="text-xs font-bold text-white" data-id="element-1057">
                      {trendData.coverage[hoveredMonth]}%
                    </span>
                  </div>}
              </div>}
          </div>
          {/* X-axis labels */}
          <div className="absolute bottom-0 left-8 right-0 flex justify-between text-xs text-gray-500 px-2 pb-2" data-id="element-1058">
            {trendData.months.map((month, index) => <span key={month} className={`cursor-pointer transition-colors ${hoveredMonth === index ? 'text-cyan-400 font-semibold' : ''}`} onMouseEnter={() => setHoveredMonth(index)} onMouseLeave={() => setHoveredMonth(null)} data-id="element-1059">
                {month}
              </span>)}
          </div>
        </div>
        {/* Interactive Legend */}
        <div className="mt-4 flex items-center justify-center space-x-6" data-id="element-1060">
          <button onClick={() => setSelectedTrendMetric(selectedTrendMetric === 'response' ? 'all' : 'response')} className={`flex items-center px-3 py-2 rounded-lg transition-all ${selectedTrendMetric === 'all' || selectedTrendMetric === 'response' ? 'bg-cyan-900/30 border border-cyan-700' : 'bg-gray-800 border border-gray-700 opacity-50 hover:opacity-100'}`} data-id="element-1061">
            <div className="w-3 h-1 bg-cyan-500 mr-2" data-id="element-1062"></div>
            <span className="text-xs text-gray-300" data-id="element-1063">Response Rate</span>
            <TrendingUp className="h-3 w-3 ml-2 text-green-400" data-id="element-1064" />
          </button>
          <button onClick={() => setSelectedTrendMetric(selectedTrendMetric === 'products' ? 'all' : 'products')} className={`flex items-center px-3 py-2 rounded-lg transition-all ${selectedTrendMetric === 'all' || selectedTrendMetric === 'products' ? 'bg-green-900/30 border border-green-700' : 'bg-gray-800 border border-gray-700 opacity-50 hover:opacity-100'}`} data-id="element-1065">
            <div className="w-3 h-1 bg-green-500 mr-2" data-id="element-1066"></div>
            <span className="text-xs text-gray-300" data-id="element-1067">Intel Products</span>
            <TrendingUp className="h-3 w-3 ml-2 text-green-400" data-id="element-1068" />
          </button>
          <button onClick={() => setSelectedTrendMetric(selectedTrendMetric === 'coverage' ? 'all' : 'coverage')} className={`flex items-center px-3 py-2 rounded-lg transition-all ${selectedTrendMetric === 'all' || selectedTrendMetric === 'coverage' ? 'bg-blue-900/30 border border-blue-700' : 'bg-gray-800 border border-gray-700 opacity-50 hover:opacity-100'}`} data-id="element-1069">
            <div className="w-3 h-1 bg-blue-500 mr-2" data-id="element-1070"></div>
            <span className="text-xs text-gray-300" data-id="element-1071">Coverage</span>
            <TrendingUp className="h-3 w-3 ml-2 text-green-400" data-id="element-1072" />
          </button>
        </div>
        {/* Statistics Cards */}
        <div className="mt-4 grid grid-cols-3 gap-4" data-id="element-1073">
          <div className="text-center p-4 bg-gradient-to-br from-cyan-900/20 to-cyan-800/10 border border-cyan-800/30 rounded-lg" data-id="element-1074">
            <p className="text-xs text-gray-400 mb-1" data-id="element-1075">Average Response</p>
            <p className="text-2xl font-bold text-cyan-400 mt-1" data-id="element-1076">86%</p>
            <div className="flex items-center justify-center mt-2" data-id="element-1077">
              <TrendingUp className="h-3 w-3 text-green-400 mr-1" data-id="element-1078" />
              <span className="text-xs text-green-400" data-id="element-1079">+8% vs last period</span>
            </div>
          </div>
          <div className="text-center p-4 bg-gradient-to-br from-green-900/20 to-green-800/10 border border-green-800/30 rounded-lg" data-id="element-1080">
            <p className="text-xs text-gray-400 mb-1" data-id="element-1081">Intel Products</p>
            <p className="text-2xl font-bold text-green-400 mt-1" data-id="element-1082">85%</p>
            <div className="flex items-center justify-center mt-2" data-id="element-1083">
              <TrendingUp className="h-3 w-3 text-green-400 mr-1" data-id="element-1084" />
              <span className="text-xs text-green-400" data-id="element-1085">+14% improvement</span>
            </div>
          </div>
          <div className="text-center p-4 bg-gradient-to-br from-blue-900/20 to-blue-800/10 border border-blue-800/30 rounded-lg" data-id="element-1086">
            <p className="text-xs text-gray-400 mb-1" data-id="element-1087">Coverage</p>
            <p className="text-2xl font-bold text-blue-400 mt-1" data-id="element-1088">89%</p>
            <div className="flex items-center justify-center mt-2" data-id="element-1089">
              <TrendingUp className="h-3 w-3 text-green-400 mr-1" data-id="element-1090" />
              <span className="text-xs text-green-400" data-id="element-1091">+9% expansion</span>
            </div>
          </div>
        </div>
      </Card>
    </div>;
};
export default PerformanceCockpit;